import { useState, type ReactNode } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Check, Copy, Fingerprint, KeyRound, Loader2, Lock, LockOpen, ShieldCheck, Trash2 } from "lucide-react"
import { useActiveOrg } from "@/core/active-event"
import { ensureSession } from "@/core/auth"
import { orgMembersQueryOptions } from "@/core/admin"
import {
  approveVaultAccess,
  enrollVaultPasskey,
  getVaultStatus,
  listMyVaultPasskeys,
  listVaultAccessRequests,
  lockVault,
  recoverWithCode,
  removeVaultPasskey,
  requestVaultAccess,
  setUpVault,
  unlockVault,
  unlockVaultWithPasskey,
  vaultPasskeySupport,
  type VaultPasskey,
} from "@/core/vault"
import { friendlyError, useVaultUnlocked } from "@/components/vault/VaultReveal"
import {
  Card,
  Field,
  GhostButton,
  Pill,
  PrimaryButton,
  SectionPage,
  TextInput,
} from "@/components/settings/primitives"

export const Route = createFileRoute("/$orgSlug/settings/vault")({
  component: VaultSettings,
})

const MIN_PASSPHRASE = 12

// The page has two scopes that must read as separate things (scope doc §5.6):
//   "Organization vault" — one key for the whole org, admin-only controls.
//   "Your access"        — how *this* member unlocks it; never affects others.

function VaultSettings() {
  const { organizationId } = useActiveOrg()
  const me = useQuery({ queryKey: ["session", "me"], queryFn: () => ensureSession(), staleTime: 30_000 })
  const members = useQuery(orgMembersQueryOptions(organizationId))
  const status = useQuery({
    queryKey: ["vault-status", organizationId],
    queryFn: () => getVaultStatus(organizationId!),
    enabled: !!organizationId,
  })
  const myRole = members.data?.members.find((m) => m.userId === me.data?.id)?.role
  const isAdmin = myRole === "admin" || myRole === "owner"

  return (
    <SectionPage
      title="Secure vault"
      description="End-to-end encrypted storage for passport, ID and visa numbers. Values are encrypted on the attendee's device; Attend stores only ciphertext and can't read them. Each person unlocks with their own passphrase or passkey."
    >
      {!organizationId || status.isLoading || members.isLoading ? (
        <Card>
          <Loader2 className="h-4 w-4 animate-spin text-plum-700/50" />
        </Card>
      ) : status.error ? (
        <Card>
          <p className="text-[13px] text-red-600">{friendlyError(status.error)}</p>
        </Card>
      ) : (
        <>
          {isAdmin && me.data && (
            <Area
              title="Organization vault"
              badge="Admins only"
              description="One vault key shared by the whole organization. Admins create it, approve who may use it, and hold its recovery code."
            >
              {status.data!.configured ? (
                <>
                  <OrgStatusCard organizationId={organizationId} />
                  <RequestsCard
                    organizationId={organizationId}
                    hasAccess={status.data!.hasAccess}
                    names={new Map((members.data?.members ?? []).map((m) => [m.userId, m.user.name || m.user.email]))}
                  />
                  <RecoveryCard organizationId={organizationId} userId={me.data.id} />
                </>
              ) : (
                <SetUpFlow organizationId={organizationId} userId={me.data.id} />
              )}
            </Area>
          )}

          <Area
            title="Your access"
            description="How you unlock the vault on this device. Your passphrase and passkeys are yours alone — teammates set up their own."
          >
            {!status.data!.configured ? (
              <Card title="Not set up yet">
                <p className="text-[13px] text-plum-700/70 dark:text-plum-200/55">
                  {isAdmin
                    ? "Create the organization vault above; you'll set up your own access as part of that."
                    : "An organization admin needs to set up the secure vault before secure form fields can be used."}
                </p>
              </Card>
            ) : (
              <>
                <AccessCard
                  organizationId={organizationId}
                  hasAccess={status.data!.hasAccess}
                  enrolled={!!status.data!.enrollment}
                />
                {me.data && <PasskeysCard organizationId={organizationId} userId={me.data.id} hasAccess={status.data!.hasAccess} />}
              </>
            )}
          </Area>
        </>
      )}
    </SectionPage>
  )
}

// A labelled area of the page grouping several cards under one scope.
function Area({
  title,
  badge,
  description,
  children,
}: {
  title: string
  badge?: string
  description?: string
  children: ReactNode
}) {
  return (
    <section aria-label={title} className="space-y-3">
      <div className="pt-1">
        <div className="flex items-center gap-2">
          <h2 className="text-[15px] font-semibold tracking-tight text-plum-900 dark:text-plum-50">{title}</h2>
          {badge && <Pill tone="neutral">{badge}</Pill>}
        </div>
        {description && (
          <p className="mt-0.5 text-[12.5px] text-plum-700/60 dark:text-plum-200/45">{description}</p>
        )}
      </div>
      {children}
    </section>
  )
}

function PassphraseFields({
  value,
  confirm,
  onChange,
  onConfirm,
}: {
  value: string
  confirm: string
  onChange: (v: string) => void
  onConfirm: (v: string) => void
}) {
  return (
    <>
      <Field
        label="Your vault passphrase"
        htmlFor="vault-passphrase"
        hint={`At least ${MIN_PASSPHRASE} characters. Not your Attend password — it never leaves this browser, and Attend can't reset it. Each teammate sets their own.`}
      >
        <TextInput id="vault-passphrase" type="password" autoComplete="new-password" value={value} onChange={(e) => onChange(e.target.value)} />
      </Field>
      <Field label="Confirm passphrase" htmlFor="vault-passphrase-confirm">
        <TextInput id="vault-passphrase-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => onConfirm(e.target.value)} />
      </Field>
    </>
  )
}

function passphraseProblem(p: string, c: string): string | null {
  if (p.length < MIN_PASSPHRASE) return `Use at least ${MIN_PASSPHRASE} characters.`
  if (p !== c) return "The passphrases don't match."
  return null
}

// ── Organization vault ───────────────────────────────────────────────────────

function OrgStatusCard({ organizationId }: { organizationId: string }) {
  // Same key as RequestsCard, so react-query makes a single request.
  const requests = useQuery({
    queryKey: ["vault-requests", organizationId],
    queryFn: () => listVaultAccessRequests(organizationId),
  })
  const pending = requests.data?.length
  return (
    <Card>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="inline-flex items-center gap-2 text-[13px] text-plum-800 dark:text-plum-100">
          <ShieldCheck className="h-4 w-4 text-emerald-600" /> Vault configured
        </span>
        {requests.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin text-plum-700/50" />
        ) : pending != null ? (
          <Pill tone={pending > 0 ? "amber" : "neutral"}>
            {pending === 0 ? "No access requests pending" : `${pending} access request${pending === 1 ? "" : "s"} pending`}
          </Pill>
        ) : null}
      </div>
    </Card>
  )
}

/**
 * Three steps: (1) explain the org-wide vault, (2) the admin's own passphrase,
 * (3) the organization recovery code. `setUpVault` creates the key, enrols
 * the admin and escrows the recovery code in one call, so nothing is sent
 * until step 2 is confirmed.
 */
function SetUpFlow({ organizationId, userId }: { organizationId: string; userId: string }) {
  const qc = useQueryClient()
  const [step, setStep] = useState<1 | 2>(1)
  const [p, setP] = useState("")
  const [c, setC] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const problem = passphraseProblem(p, c)

  if (recoveryCode) {
    return (
      <Card
        title="Organization recovery code"
        description="This code is for every admin, not just you. If every admin loses their vault passphrase, it is the only way back in — Attend can't recover it. Store it where the organization's admins can find it. It won't be shown again."
        footer={
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-[12.5px] text-plum-800 dark:text-plum-100">
              <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
              I've stored this code somewhere safe
            </label>
            <PrimaryButton
              disabled={!saved}
              onClick={() => void qc.invalidateQueries({ queryKey: ["vault-status", organizationId] })}
            >
              Done
            </PrimaryButton>
          </div>
        }
      >
        <StepLabel step={3} />
        <CopyCode code={recoveryCode} />
      </Card>
    )
  }

  if (step === 1) {
    return (
      <Card
        title="Create your organization's vault"
        description="One encrypted vault for the whole organization. Nothing to type here — you'll set your own passphrase in the next step."
        footer={
          <div className="flex items-center justify-end gap-3">
            <PrimaryButton onClick={() => setStep(2)}>
              <ShieldCheck className="h-4 w-4" /> Continue
            </PrimaryButton>
          </div>
        }
      >
        <StepLabel step={1} />
        <ul className="list-disc space-y-1 pl-5 text-[13px] text-plum-700/70 dark:text-plum-200/55">
          <li>Secure form fields and documents across all your events are encrypted to this vault.</li>
          <li>Teammates request access and an admin approves them; each person unlocks with their own passphrase or passkey.</li>
          <li>You'll receive an organization recovery code at the end — keep it with the other admins.</li>
        </ul>
      </Card>
    )
  }

  return (
    <Card
      title="Set up your own access"
      description="This passphrase is yours. It unlocks the vault for you on this device; each teammate sets their own."
      footer={
        <div className="flex items-center justify-end gap-3">
          {error && <span className="text-[12px] text-red-600">{error}</span>}
          <GhostButton disabled={busy} onClick={() => setStep(1)}>
            Back
          </GhostButton>
          <PrimaryButton
            disabled={!!problem || busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                const res = await setUpVault({ organizationId, userId, passphrase: p })
                setRecoveryCode(res.recoveryCode)
              } catch (e) {
                setError(friendlyError(e))
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Create vault
          </PrimaryButton>
        </div>
      }
    >
      <StepLabel step={2} />
      <PassphraseFields value={p} confirm={c} onChange={setP} onConfirm={setC} />
      {p && c && problem && <p className="text-[12px] text-amber-700">{problem}</p>}
    </Card>
  )
}

function StepLabel({ step }: { step: 1 | 2 | 3 }) {
  return (
    <div>
      <Pill tone="accent">Step {step} of 3</Pill>
    </div>
  )
}

function CopyCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-3 rounded-lg border border-plum-200/70 bg-plum-50/50 px-4 py-3 dark:border-white/10 dark:bg-white/[0.03]">
      <span className="flex-1 font-mono text-[15px] tracking-wide text-plum-900 select-all dark:text-plum-50">{code}</span>
      <GhostButton
        onClick={() => {
          void navigator.clipboard.writeText(code)
          setCopied(true)
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
      </GhostButton>
    </div>
  )
}

function RequestsCard({
  organizationId,
  hasAccess,
  names,
}: {
  organizationId: string
  hasAccess: boolean
  names: Map<string, string>
}) {
  const qc = useQueryClient()
  const isUnlocked = useVaultUnlocked(organizationId)
  const requests = useQuery({
    queryKey: ["vault-requests", organizationId],
    queryFn: () => listVaultAccessRequests(organizationId),
  })
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  return (
    <Card
      title="Access requests"
      description={
        isUnlocked
          ? "Approving re-wraps the vault key to the requester in this browser."
          : hasAccess
            ? "Unlock your vault under “Your access” below to approve requests."
            : "You need vault access yourself before you can approve others."
      }
      footer={error ? <span className="text-[12px] text-red-600">{error}</span> : undefined}
    >
      {requests.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-plum-700/50" />
      ) : (requests.data ?? []).length === 0 ? (
        <p className="text-[13px] text-plum-700/60 dark:text-plum-200/45">No pending requests.</p>
      ) : (
        <ul className="divide-y divide-plum-200/50 dark:divide-white/10">
          {requests.data!.map((r) => (
            <li key={r.userId} className="flex items-center justify-between gap-3 py-2">
              <div className="min-w-0">
                <p className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
                  {names.get(r.userId) ?? r.userId}
                </p>
                {!r.enrollment && (
                  <p className="text-[11.5px] text-amber-700">Out of date — ask them to request again.</p>
                )}
              </div>
              <PrimaryButton
                disabled={!isUnlocked || !r.enrollment || busyId != null}
                onClick={async () => {
                  setBusyId(r.userId)
                  setError(null)
                  try {
                    await approveVaultAccess({ organizationId, request: r })
                    await qc.invalidateQueries({ queryKey: ["vault-requests", organizationId] })
                  } catch (e) {
                    setError(friendlyError(e))
                  } finally {
                    setBusyId(null)
                  }
                }}
              >
                {busyId === r.userId && <Loader2 className="h-4 w-4 animate-spin" />} Approve
              </PrimaryButton>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

function RecoveryCard({ organizationId, userId }: { organizationId: string; userId: string }) {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const [code, setCode] = useState("")
  const [p, setP] = useState("")
  const [c, setC] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const problem = passphraseProblem(p, c)

  if (!open) {
    return (
      <Card
        title="Organization recovery"
        description="The recovery code issued when the vault was created lets any admin regain access if every admin loses their passphrase."
      >
        <button type="button" onClick={() => setOpen(true)} className="text-[12.5px] text-plum-700/70 underline dark:text-plum-200/55">
          Lost access? Use the recovery code to set a new passphrase
        </button>
      </Card>
    )
  }
  return (
    <Card
      title="Recover with code"
      description="Enter the organization recovery code saved when the vault was set up, and choose a new vault passphrase for yourself."
      footer={
        <div className="flex items-center justify-end gap-3">
          {error && <span className="text-[12px] text-red-600">{error}</span>}
          <GhostButton onClick={() => setOpen(false)}>Cancel</GhostButton>
          <PrimaryButton
            disabled={!code || !!problem || busy}
            onClick={async () => {
              setBusy(true)
              setError(null)
              try {
                await recoverWithCode({ organizationId, userId, recoveryCode: code, newPassphrase: p })
                setOpen(false)
                await qc.invalidateQueries({ queryKey: ["vault-status", organizationId] })
              } catch (e) {
                setError(friendlyError(e))
              } finally {
                setBusy(false)
              }
            }}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Recover
          </PrimaryButton>
        </div>
      }
    >
      <Field label="Recovery code" htmlFor="vault-recovery-code">
        <TextInput id="vault-recovery-code" autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} />
      </Field>
      <PassphraseFields value={p} confirm={c} onChange={setP} onConfirm={setC} />
    </Card>
  )
}

// ── Your access ──────────────────────────────────────────────────────────────

function AccessCard({
  organizationId,
  hasAccess,
  enrolled,
}: {
  organizationId: string
  hasAccess: boolean
  enrolled: boolean
}) {
  const qc = useQueryClient()
  const isUnlocked = useVaultUnlocked(organizationId)
  const [p, setP] = useState("")
  const [c, setC] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [rerequest, setRerequest] = useState(false)
  const passkeys = useQuery({
    queryKey: ["vault-passkeys", organizationId],
    queryFn: () => listMyVaultPasskeys(organizationId),
    enabled: hasAccess,
  })
  const canUsePasskey = vaultPasskeySupport() === "available" && (passkeys.data ?? []).length > 0

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await fn()
      setP("")
      setC("")
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  if (hasAccess && !rerequest) {
    return (
      <Card
        title="Unlock"
        description="Unlock with your vault passphrase or a passkey to reveal encrypted values. It stays unlocked in this tab for 10 minutes."
        footer={
          error ? <span className="text-[12px] text-red-600">{error}</span> : undefined
        }
      >
        {isUnlocked ? (
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[13px] text-plum-800 dark:text-plum-100">
              <LockOpen className="h-4 w-4 text-emerald-600" /> Unlocked in this tab
            </span>
            <GhostButton onClick={() => lockVault(organizationId)}>
              <Lock className="h-4 w-4" /> Lock now
            </GhostButton>
          </div>
        ) : (
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              void run(() => unlockVault({ organizationId, passphrase: p }))
            }}
          >
            <div className="flex-1">
              <Field label="Your vault passphrase" htmlFor="vault-unlock">
                <TextInput id="vault-unlock" type="password" autoComplete="current-password" value={p} onChange={(e) => setP(e.target.value)} />
              </Field>
            </div>
            <PrimaryButton type="submit" disabled={busy || !p}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Unlock
            </PrimaryButton>
            {canUsePasskey && (
              <GhostButton
                type="button"
                disabled={busy}
                onClick={() => void run(() => unlockVaultWithPasskey({ organizationId }))}
              >
                <Fingerprint className="h-4 w-4" /> Use passkey
              </GhostButton>
            )}
          </form>
        )}
        <button
          type="button"
          onClick={() => setRerequest(true)}
          className="text-[12px] text-plum-700/60 underline dark:text-plum-200/45"
        >
          Forgot your passphrase? Set a new one and request access again
        </button>
      </Card>
    )
  }

  const pending = enrolled && !hasAccess && !rerequest
  const problem = passphraseProblem(p, c)
  return (
    <Card
      title={pending ? "Access requested" : "Request access"}
      description={
        pending
          ? "An organization admin needs to approve your request. You can reveal encrypted values once they do."
          : "Choose your own vault passphrase. An admin approves the request; after that you unlock with this passphrase to reveal values."
      }
      footer={
        pending ? undefined : (
          <div className="flex items-center justify-end gap-3">
            {error && <span className="text-[12px] text-red-600">{error}</span>}
            <PrimaryButton
              disabled={!!problem || busy}
              onClick={() =>
                void run(async () => {
                  await requestVaultAccess({ organizationId, passphrase: p })
                  setRerequest(false)
                  await qc.invalidateQueries({ queryKey: ["vault-status", organizationId] })
                })
              }
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Request access
            </PrimaryButton>
          </div>
        )
      }
    >
      {pending ? (
        <div className="flex items-center justify-between">
          <Pill tone="amber">Waiting for approval</Pill>
          <button type="button" onClick={() => setRerequest(true)} className="text-[12px] text-plum-700/60 underline">
            Use a different passphrase
          </button>
        </div>
      ) : (
        <>
          <PassphraseFields value={p} confirm={c} onChange={setP} onConfirm={setC} />
          {p && c && problem && <p className="text-[12px] text-amber-700">{problem}</p>}
        </>
      )}
    </Card>
  )
}

function PasskeysCard({ organizationId, userId, hasAccess }: { organizationId: string; userId: string; hasAccess: boolean }) {
  const qc = useQueryClient()
  const isUnlocked = useVaultUnlocked(organizationId)
  const passkeys = useQuery({
    queryKey: ["vault-passkeys", organizationId],
    queryFn: () => listMyVaultPasskeys(organizationId),
  })
  const support = vaultPasskeySupport()
  const canAdd = support === "available" && hasAccess
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState("")
  const [passphrase, setPassphrase] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const helper =
    support === "needs-backend-update"
      ? "Passkey unlock isn't available yet — it's coming in an upcoming update."
      : support === "unsupported"
        ? "This browser or device can't unlock the vault with a passkey."
        : !hasAccess
          ? "You need vault access before you can add a passkey."
          : null
  // The wrap needs your master key: present in memory after an unlock, else
  // re-derived from the passphrase right here.
  const needsPassphrase = !isUnlocked

  const add = async () => {
    setBusy(true)
    setError(null)
    try {
      await enrollVaultPasskey({
        organizationId,
        userId,
        label: label.trim() || undefined,
        passphrase: needsPassphrase ? passphrase : undefined,
      })
      setAdding(false)
      setLabel("")
      setPassphrase("")
      await qc.invalidateQueries({ queryKey: ["vault-passkeys", organizationId] })
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (id: string) => {
    setBusy(true)
    setError(null)
    try {
      await removeVaultPasskey({ organizationId, id })
      await qc.invalidateQueries({ queryKey: ["vault-passkeys", organizationId] })
    } catch (e) {
      setError(friendlyError(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card
      title="Passkeys"
      description="Unlock the vault on this device with Face ID, Touch ID, Windows Hello or a security key. A passkey is yours alone — it doesn't change anyone else's access, and your passphrase keeps working."
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-[12px] text-plum-700/60 dark:text-plum-200/45">
            {error ? <span className="text-red-600">{error}</span> : helper}
          </span>
          {!adding ? (
            <PrimaryButton disabled={!canAdd || busy} onClick={() => setAdding(true)}>
              <Fingerprint className="h-4 w-4" /> Add a passkey
            </PrimaryButton>
          ) : (
            <div className="flex items-center gap-2">
              <GhostButton disabled={busy} onClick={() => { setAdding(false); setError(null) }}>Cancel</GhostButton>
              <PrimaryButton disabled={busy || (needsPassphrase && !passphrase)} onClick={() => void add()}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />} Create passkey
              </PrimaryButton>
            </div>
          )}
        </div>
      }
    >
      {adding && (
        <form
          className="mb-3 flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            void add()
          }}
        >
          <div className="min-w-40 flex-1">
            <Field label="Name (optional)" htmlFor="vault-passkey-label" hint="e.g. MacBook Touch ID, YubiKey">
              <TextInput id="vault-passkey-label" autoComplete="off" value={label} onChange={(e) => setLabel(e.target.value)} />
            </Field>
          </div>
          {needsPassphrase && (
            <div className="min-w-40 flex-1">
              <Field label="Your vault passphrase" htmlFor="vault-passkey-passphrase" hint="Needed once to link the passkey to your keys.">
                <TextInput id="vault-passkey-passphrase" type="password" autoComplete="current-password" value={passphrase} onChange={(e) => setPassphrase(e.target.value)} />
              </Field>
            </div>
          )}
        </form>
      )}
      {passkeys.isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-plum-700/50" />
      ) : passkeys.error ? (
        <p className="text-[13px] text-red-600">{friendlyError(passkeys.error)}</p>
      ) : (passkeys.data ?? []).length === 0 ? (
        <p className="text-[13px] text-plum-700/60 dark:text-plum-200/45">No passkeys yet.</p>
      ) : (
        <ul className="divide-y divide-plum-200/50 dark:divide-white/10">
          {passkeys.data!.map((k) => (
            <PasskeyRow key={k.id} passkey={k} onRemove={busy ? undefined : () => void remove(k.id)} />
          ))}
        </ul>
      )}
    </Card>
  )
}

function PasskeyRow({ passkey, onRemove }: { passkey: VaultPasskey; onRemove?: () => void }) {
  const fmt = (iso: string) => new Date(iso).toLocaleDateString()
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="min-w-0">
        <p className="truncate text-[13px] font-semibold text-plum-900 dark:text-plum-50">
          {passkey.label ?? "Passkey"}
        </p>
        <p className="text-[11.5px] text-plum-700/55 dark:text-plum-200/40">
          Added {fmt(passkey.createdAt)}
          {passkey.lastUsedAt ? ` · Last used ${fmt(passkey.lastUsedAt)}` : " · Never used"}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <Pill tone={passkey.backedUp ? "neutral" : "accent"}>{passkey.backedUp ? "Synced" : "This device only"}</Pill>
        <button
          type="button"
          disabled={!onRemove}
          onClick={onRemove}
          title="Remove this passkey from the vault"
          className="text-plum-700/50 hover:text-red-600 disabled:opacity-40 dark:text-plum-200/40"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </li>
  )
}
